import EventEmitter from "events";
import { getConnectionId, } from "@/stores/workflowConnectionStore";
import { assertDefined } from "@/utils/assertions";
import { ANY_COLLECTION_TYPE_DESCRIPTION, CollectionTypeDescription, NULL_COLLECTION_TYPE_DESCRIPTION, } from "./collectionTypeDescription";
export class ConnectionAcceptable {
    constructor(canAccept, reason) {
        Object.defineProperty(this, "reason", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "canAccept", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.canAccept = canAccept;
        this.reason = reason;
    }
}
class Terminal extends EventEmitter {
    constructor(attr) {
        super();
        Object.defineProperty(this, "connectionStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stepStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "name", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "multiple", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stepId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "terminalType", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "datatypesMapper", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "localMapOver", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.connectionStore = attr.connectionStore;
        this.stepStore = attr.stepStore;
        this.stepId = attr.stepId;
        this.name = attr.name;
        this.multiple = false;
        this.terminalType = "input";
        this.datatypesMapper = attr.datatypesMapper;
        this.localMapOver = NULL_COLLECTION_TYPE_DESCRIPTION;
    }
    get id() {
        return `node-${this.stepId}-${this.terminalType}-${this.name}`;
    }
    get connections() {
        return this.connectionStore.getConnectionsForTerminal(this.id);
    }
    get mapOver() {
        return this.stepStore.stepMapOver[this.stepId] || NULL_COLLECTION_TYPE_DESCRIPTION;
    }
    connect(other) {
        const connection = {
            input: { stepId: this.stepId, name: this.name, connectorType: "input" },
            output: { stepId: other.stepId, name: other.name, connectorType: "output" },
        };
        this.connectionStore.addConnection(connection);
    }
    disconnect(other) {
        let connection;
        if (other instanceof Terminal) {
            connection = {
                input: { stepId: this.stepId, name: this.name, connectorType: "input" },
                output: { stepId: other.stepId, name: other.name, connectorType: "output" },
            };
        }
        else {
            connection = other;
        }
        this.connectionStore.removeConnection(getConnectionId(connection));
        this.resetMappingIfNeeded(connection);
    }
    setMapOver(val) {
        var _a;
        // we use this method to determine the map over status based on all terminals connected to this step
        let outputVal = val;
        if (this.multiple) {
            // emulate list input
            const description = new CollectionTypeDescription("list");
            if (val.collectionType === description.collectionType) {
                // No mapping over necessary
                return;
            }
            outputVal = val.effectiveMapOver(description);
        }
        const effectiveMapOver = this._effectiveMapOver(outputVal);
        if (!this.localMapOver.equal(effectiveMapOver)) {
            this.stepStore.changeStepInputMapOver(this.stepId, this.name, effectiveMapOver);
            this.localMapOver = effectiveMapOver;
        }
        if (!this.mapOver.equal(effectiveMapOver) &&
            (effectiveMapOver.isCollection ||
                !Object.values((_a = this.stepStore.stepInputMapOver[this.stepId]) !== null && _a !== void 0 ? _a : []).find((mapOver) => mapOver.isCollection))) {
            this.stepStore.changeStepMapOver(this.stepId, effectiveMapOver);
        }
    }
    _effectiveMapOver(otherCollectionType) {
        return otherCollectionType;
    }
    isMappedOver() {
        return Boolean(this.mapOver.isCollection);
    }
    resetMapping(_connection) {
        this.stepStore.changeStepMapOver(this.stepId, NULL_COLLECTION_TYPE_DESCRIPTION);
        this.stepStore.resetStepInputMapOver(this.stepId);
    }
    hasConnectedMappedInputTerminals() {
        // check if step has connected and mapped input terminals ... should maybe be on step/node ?
        const connections = this.connectionStore.getConnectionsForStep(this.stepId);
        return connections.some((connection) => { var _a; return connection.input.stepId === this.stepId && ((_a = this.stepStore.stepMapOver[this.stepId]) === null || _a === void 0 ? void 0 : _a.collectionType); });
    }
    _getOutputConnections() {
        return this.connectionStore.getConnectionsForStep(this.stepId).filter((connection) => {
            return connection.output.stepId === this.stepId;
        });
    }
    hasConnectedOutputTerminals() {
        // Does this step/node have connected connections
        return this._getOutputConnections().length > 0;
    }
    hasMappedOverInputTerminals() {
        var _a;
        return Boolean((_a = this.stepStore.stepMapOver[this.stepId]) === null || _a === void 0 ? void 0 : _a.collectionType);
    }
    resetMappingIfNeeded(connection) {
        const mapOver = this.mapOver;
        if (!mapOver.isCollection) {
            return;
        }
        // No output terminals are counting on this being mapped
        // over if connected inputs are still mapped over or if none
        // of the outputs are connected...
        const reset = !this.hasConnectedOutputTerminals();
        if (reset) {
            this.resetMapping(connection);
        }
    }
}
class BaseInputTerminal extends Terminal {
    constructor(attr) {
        var _a;
        super(attr);
        Object.defineProperty(this, "datatypes", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "optional", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "localMapOver", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.datatypes = attr.input.datatypes;
        this.multiple = attr.input.multiple;
        this.optional = attr.input.optional;
        if (this.stepStore.stepInputMapOver[this.stepId] && ((_a = this.stepStore.stepInputMapOver[this.stepId]) === null || _a === void 0 ? void 0 : _a[this.name])) {
            this.localMapOver = this.stepStore.stepInputMapOver[this.stepId][this.name];
        }
        else {
            this.localMapOver = NULL_COLLECTION_TYPE_DESCRIPTION;
        }
    }
    connect(other) {
        super.connect(other);
        this.setDefaultMapOver(other);
    }
    setDefaultMapOver(other) {
        const otherCollectionType = this._otherCollectionType(other);
        if (otherCollectionType.isCollection) {
            return this.setMapOver(otherCollectionType);
        }
    }
    getStepMapOver() {
        this.getConnectedTerminals().forEach((other) => {
            this.setDefaultMapOver(other);
        });
    }
    canAccept(outputTerminal) {
        if (this.stepId == outputTerminal.stepId) {
            return new ConnectionAcceptable(false, "Cannot connect output to input of same step.");
        }
        if (this._inputFilled()) {
            return new ConnectionAcceptable(false, "Input already filled with another connection, delete it before connecting another output.");
        }
        else {
            return this.attachable(outputTerminal);
        }
    }
    attachable(_terminal) {
        // TODO: provide through Mixin
        throw Error("Subclass needs to implement this");
    }
    _getOutputStepsMapOver() {
        const connections = this._getOutputConnections();
        const connectedStepIds = Array.from(new Set(connections.map((connection) => connection.output.stepId)));
        return connectedStepIds.map((stepId) => this.stepStore.stepMapOver[stepId] || NULL_COLLECTION_TYPE_DESCRIPTION);
    }
    resetMapping(connection) {
        super.resetMapping(connection);
        this.stepStore.changeStepInputMapOver(this.stepId, this.name, NULL_COLLECTION_TYPE_DESCRIPTION);
        const outputStepIds = this._getOutputTerminals().map((outputTerminal) => outputTerminal.stepId);
        if (connection) {
            outputStepIds.push(connection.output.stepId);
        }
        Array.from(new Set(outputStepIds)).forEach((stepId) => {
            const step = this.stepStore.getStep(stepId);
            if (step) {
                // step must have an output, since it is or was connected to this step
                const terminalSource = step.outputs[0];
                if (terminalSource) {
                    const terminal = terminalFactory(step.id, terminalSource, this.datatypesMapper, this.connectionStore, this.stepStore);
                    // drop mapping restrictions
                    terminal.resetMappingIfNeeded();
                    // re-establish map over through inputs
                    step.inputs.forEach((input) => {
                        terminalFactory(step.id, input, this.datatypesMapper, this.connectionStore, this.stepStore).getStepMapOver();
                    });
                }
            }
            else {
                console.error(`Invalid step. Could not fine step with id ${stepId} in store.`);
            }
        });
    }
    _getOutputTerminals() {
        return this.connectionStore.getOutputTerminalsForInputTerminal(this.id);
    }
    _getFirstOutputTerminal() {
        const outputTerminals = this._getOutputTerminals();
        if (outputTerminals.length > 0) {
            return outputTerminals[0];
        }
        return null;
    }
    isMappedOver() {
        return Boolean(this.localMapOver.isCollection);
    }
    connected() {
        return Boolean(this._getFirstOutputTerminal());
    }
    _inputFilled() {
        let inputFilled;
        if (!this.connected()) {
            inputFilled = false;
        }
        else {
            if (this.multiple) {
                // Can only attach one collection to multiple input
                // data parameter.
                inputFilled = !!this._collectionAttached();
            }
            else {
                inputFilled = true;
            }
        }
        return inputFilled;
    }
    _collectionAttached() {
        const outputTerminals = this._getOutputTerminals();
        return outputTerminals.some((outputTerminal) => {
            var _a;
            const step = this.stepStore.getStep(outputTerminal.stepId);
            if (!step) {
                console.error(`Invalid step. Could not find step with id ${outputTerminal.stepId} in store.`);
                return false;
            }
            const output = step.outputs.find((output) => output.name == outputTerminal.name);
            if (output &&
                (("collection" in output && output.collection) ||
                    ((_a = this.stepStore.stepMapOver[outputTerminal.stepId]) === null || _a === void 0 ? void 0 : _a.isCollection) ||
                    ("extensions" in output && output.extensions.indexOf("input") > 0))) {
                return true;
            }
        });
    }
    _mappingConstraints() {
        // If this is a connected terminal, return list of collection types
        // other terminals connected to node are constraining mapping to.
        if (this.mapOver.isCollection) {
            return [this.mapOver];
        }
        return this._getOutputStepsMapOver();
    }
    _producesAcceptableDatatype(other) {
        // other is a non-collection output...
        if (other instanceof OutputParameterTerminal) {
            return new ConnectionAcceptable(false, "Cannot connect workflow parameter to data input.");
        }
        return producesAcceptableDatatype(this.datatypesMapper, this.datatypes, other.datatypes);
    }
    _producesAcceptableDatatypeAndOptionalness(other) {
        if (!this.optional && !this.multiple && other.optional) {
            return new ConnectionAcceptable(false, "Cannot connect an optional output to a non-optional input");
        }
        return this._producesAcceptableDatatype(other);
    }
    _otherCollectionType(other) {
        let otherCollectionType = NULL_COLLECTION_TYPE_DESCRIPTION;
        if (other.isCollection && other.collectionType) {
            otherCollectionType = other.collectionType;
        }
        const otherMapOver = other.mapOver;
        if (otherMapOver.isCollection) {
            otherCollectionType = otherMapOver.append(otherCollectionType);
        }
        return otherCollectionType;
    }
    getConnectedTerminals() {
        return this.connections.map((connection) => {
            const outputStep = this.stepStore.getStep(connection.output.stepId);
            if (!outputStep) {
                return new InvalidOutputTerminal({
                    stepId: -1,
                    optional: false,
                    datatypes: [],
                    name: connection.output.name,
                    valid: false,
                    datatypesMapper: this.datatypesMapper,
                    connectionStore: this.connectionStore,
                    stepStore: this.stepStore,
                });
            }
            let terminalSource = outputStep.outputs.find((output) => output.name === connection.output.name);
            if (!terminalSource) {
                return new InvalidOutputTerminal({
                    stepId: -1,
                    optional: false,
                    datatypes: [],
                    name: connection.output.name,
                    valid: false,
                    datatypesMapper: this.datatypesMapper,
                    connectionStore: this.connectionStore,
                    stepStore: this.stepStore,
                });
            }
            const postJobActionKey = `ChangeDatatypeAction${connection.output.name}`;
            if ("extensions" in terminalSource &&
                outputStep.post_job_actions &&
                postJobActionKey in outputStep.post_job_actions) {
                const extensionType = outputStep.post_job_actions[postJobActionKey].action_arguments.newtype;
                terminalSource = Object.assign(Object.assign({}, terminalSource), { extensions: extensionType ? [extensionType] : [] });
            }
            return terminalFactory(outputStep.id, terminalSource, this.datatypesMapper, this.connectionStore, this.stepStore);
        });
    }
    getInvalidConnectedTerminals() {
        return this.getConnectedTerminals().filter((terminal) => {
            var _a;
            const canAccept = this.attachable(terminal);
            const connectionId = `${this.stepId}-${this.name}-${terminal.stepId}-${terminal.name}`;
            if (!canAccept.canAccept) {
                this.connectionStore.markInvalidConnection(connectionId, (_a = canAccept.reason) !== null && _a !== void 0 ? _a : "Unknown");
                return true;
            }
            else if (this.connectionStore.invalidConnections[connectionId]) {
                this.connectionStore.dropFromInvalidConnections(connectionId);
            }
            return false;
        });
    }
    destroyInvalidConnections() {
        this.getInvalidConnectedTerminals().forEach((terminal) => this.disconnect(terminal));
    }
}
export class InvalidInputTerminal extends BaseInputTerminal {
    constructor(attr) {
        super(attr);
        Object.defineProperty(this, "valid", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "localMapOver", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.valid = false;
        this.localMapOver = NULL_COLLECTION_TYPE_DESCRIPTION;
    }
    attachable(_terminal) {
        return new ConnectionAcceptable(false, "Cannot attach to invalid input. Disconnect this input.");
    }
}
// TODO: turn into InputTerminalAttachableMixin ?
export class InputTerminal extends BaseInputTerminal {
    constructor(attr) {
        super(attr);
        Object.defineProperty(this, "collection", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.collection = false;
        this.getStepMapOver();
    }
    attachable(other) {
        const otherCollectionType = this._otherCollectionType(other);
        const mapOver = this.mapOver;
        if (otherCollectionType.isCollection) {
            if (this.multiple) {
                if (this.connected() && !this._collectionAttached()) {
                    return new ConnectionAcceptable(false, "Cannot attach collections to data parameters with individual data inputs already attached.");
                }
                if (otherCollectionType.collectionType && otherCollectionType.collectionType.endsWith("paired")) {
                    return new ConnectionAcceptable(false, "Cannot attach paired inputs to multiple data parameters, only lists may be treated this way.");
                }
            }
            if (mapOver.isCollection && mapOver.canMatch(otherCollectionType)) {
                return this._producesAcceptableDatatypeAndOptionalness(other);
            }
            else if (this.multiple &&
                new CollectionTypeDescription("list").append(this.mapOver).canMatch(otherCollectionType)) {
                // This handles the special case of a list input being connected to a multiple="true" data input.
                // Nested lists would be correctly mapped over by the above condition.
                return this._producesAcceptableDatatypeAndOptionalness(other);
            }
            else {
                //  Need to check if this would break constraints...
                const mappingConstraints = this._mappingConstraints();
                if (mappingConstraints.every(otherCollectionType.canMatch.bind(otherCollectionType))) {
                    return this._producesAcceptableDatatypeAndOptionalness(other);
                }
                else {
                    if (mapOver.isCollection) {
                        // incompatible collection type attached
                        if (this.hasConnectedMappedInputTerminals()) {
                            return new ConnectionAcceptable(false, "Can't map over this input with output collection type - other inputs have an incompatible map over collection type. Disconnect inputs (and potentially outputs) and retry.");
                        }
                        else {
                            return new ConnectionAcceptable(false, "Can't map over this input with output collection type - this step has outputs defined constraining the mapping of this tool. Disconnect outputs and retry.");
                        }
                    }
                    else {
                        return new ConnectionAcceptable(false, "Can't map over this input with output collection type - an output of this tool is mapped over constraining this input. Disconnect output(s) and retry.");
                    }
                }
            }
        }
        else {
            if (this.localMapOver.isCollection) {
                return new ConnectionAcceptable(false, "Cannot attach non-collection output to mapped over input, consider disconnecting inputs and outputs to reset this input's mapping.");
            }
        }
        return this._producesAcceptableDatatypeAndOptionalness(other);
    }
}
export class InputParameterTerminal extends BaseInputTerminal {
    constructor(attr) {
        super(attr);
        Object.defineProperty(this, "type", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.type = attr.type;
        this.getStepMapOver();
    }
    effectiveType(parameterType) {
        return parameterType == "select" ? "text" : parameterType;
    }
    attachable(other) {
        const effectiveThisType = this.effectiveType(this.type);
        const otherType = ("type" in other && other.type) || "data";
        const effectiveOtherType = this.effectiveType(otherType);
        const canAccept = effectiveThisType === effectiveOtherType;
        return new ConnectionAcceptable(canAccept, canAccept ? null : `Cannot attach a ${effectiveOtherType} parameter to a ${effectiveThisType} input`);
    }
}
export class InputCollectionTerminal extends BaseInputTerminal {
    constructor(attr) {
        super(attr);
        Object.defineProperty(this, "collection", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "collectionTypes", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.multiple = false;
        this.collection = true;
        this.collectionTypes = attr.collection_types
            ? attr.collection_types.map((collectionType) => new CollectionTypeDescription(collectionType))
            : [];
        if (!this.collectionTypes.length) {
            this.collectionTypes.push(ANY_COLLECTION_TYPE_DESCRIPTION);
        }
        this.getStepMapOver();
    }
    _effectiveMapOver(otherCollectionType) {
        const collectionTypes = this.collectionTypes;
        const canMatch = collectionTypes.some((collectionType) => collectionType.canMatch(otherCollectionType));
        if (!canMatch) {
            for (const collectionTypeIndex in collectionTypes) {
                const collectionType = collectionTypes[collectionTypeIndex];
                if (otherCollectionType.canMapOver(collectionType)) {
                    const effectiveMapOver = otherCollectionType.effectiveMapOver(collectionType);
                    if (effectiveMapOver != NULL_COLLECTION_TYPE_DESCRIPTION) {
                        return effectiveMapOver;
                    }
                }
            }
        }
        return NULL_COLLECTION_TYPE_DESCRIPTION;
    }
    _effectiveCollectionTypes() {
        return this.collectionTypes.map((t) => this.localMapOver.append(t));
    }
    attachable(other) {
        const otherCollectionType = this._otherCollectionType(other);
        if (otherCollectionType.isCollection) {
            const effectiveCollectionTypes = this._effectiveCollectionTypes();
            const mapOver = this.mapOver;
            const canMatch = effectiveCollectionTypes.some((effectiveCollectionType) => effectiveCollectionType.canMatch(otherCollectionType));
            if (canMatch) {
                // Only way a direct match...
                return this._producesAcceptableDatatypeAndOptionalness(other);
                // Otherwise we need to mapOver
            }
            else if (mapOver.isCollection) {
                // In this case, mapOver already set and we didn't match skipping...
                if (this.hasConnectedMappedInputTerminals()) {
                    return new ConnectionAcceptable(false, "Can't map over this input with output collection type - other inputs have an incompatible map over collection type. Disconnect inputs (and potentially outputs) and retry.");
                }
                else {
                    return new ConnectionAcceptable(false, "Can't map over this input with output collection type - this step has outputs defined constraining the mapping of this tool. Disconnect outputs and retry.");
                }
            }
            else if (this.collectionTypes.some((collectionType) => otherCollectionType.canMapOver(collectionType))) {
                // we're not mapped over - but hey maybe we could be... lets check.
                const effectiveMapOver = this._effectiveMapOver(otherCollectionType);
                //  Need to check if this would break constraints...
                const mappingConstraints = this._mappingConstraints();
                if (mappingConstraints.every((d) => effectiveMapOver.canMatch(d))) {
                    return this._producesAcceptableDatatypeAndOptionalness(other);
                }
                else {
                    return new ConnectionAcceptable(false, "Can't map over this input with output collection type - this step has outputs defined constraining the mapping of this tool. Disconnect outputs and retry.");
                }
            }
            else {
                return new ConnectionAcceptable(false, "Incompatible collection type(s) for attachment.");
            }
        }
        else {
            return new ConnectionAcceptable(false, "Cannot attach a data output to a collection input.");
        }
    }
}
class BaseOutputTerminal extends Terminal {
    constructor(attr) {
        var _a;
        super(attr);
        Object.defineProperty(this, "datatypes", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "optional", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "isCollection", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "collectionType", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "type", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.datatypes = attr.datatypes;
        this.optional = attr.optional || Boolean((_a = this.stepStore.getStep(this.stepId)) === null || _a === void 0 ? void 0 : _a.when);
        this.terminalType = "output";
    }
    getConnectedTerminals() {
        return this.connections.map((connection) => {
            const inputStep = this.stepStore.getStep(connection.input.stepId);
            assertDefined(inputStep, `Invalid step. Could not find step with id ${connection.input.stepId} in store.`);
            const extraStepInput = this.stepStore.getStepExtraInputs(inputStep.id);
            const terminalSource = [...extraStepInput, ...inputStep.inputs].find((input) => input.name === connection.input.name);
            if (!terminalSource) {
                return new InvalidInputTerminal({
                    valid: false,
                    name: connection.input.name,
                    stepId: connection.input.stepId,
                    datatypesMapper: this.datatypesMapper,
                    input_type: "dataset",
                    input: {
                        datatypes: [],
                        optional: false,
                        multiple: false,
                    },
                    connectionStore: this.connectionStore,
                    stepStore: this.stepStore,
                });
            }
            return terminalFactory(inputStep.id, terminalSource, this.datatypesMapper, this.connectionStore, this.stepStore);
        });
    }
    getInvalidConnectedTerminals() {
        return this.getConnectedTerminals().filter((terminal) => {
            var _a;
            const canAccept = terminal.attachable(this);
            const connectionId = `${terminal.stepId}-${terminal.name}-${this.stepId}-${this.name}`;
            if (!canAccept.canAccept) {
                this.connectionStore.markInvalidConnection(connectionId, (_a = canAccept.reason) !== null && _a !== void 0 ? _a : "Unknown");
                return true;
            }
            else if (this.connectionStore.invalidConnections[connectionId]) {
                this.connectionStore.dropFromInvalidConnections(connectionId);
            }
            return false;
        });
    }
    destroyInvalidConnections() {
        this.getConnectedTerminals().forEach((terminal) => {
            if (!terminal.attachable(this).canAccept) {
                terminal.disconnect(this);
            }
        });
    }
    validInputTerminals() {
        const validInputTerminals = [];
        Object.values(this.stepStore.steps).map((step) => {
            var _a;
            (_a = step.inputs) === null || _a === void 0 ? void 0 : _a.forEach((input) => {
                const inputTerminal = terminalFactory(step.id, input, this.datatypesMapper, this.connectionStore, this.stepStore);
                if (inputTerminal.canAccept(this).canAccept) {
                    validInputTerminals.push(inputTerminal);
                }
            });
        });
        return validInputTerminals;
    }
}
export class OutputTerminal extends BaseOutputTerminal {
}
export class OutputCollectionTerminal extends BaseOutputTerminal {
    constructor(attr) {
        super(attr);
        Object.defineProperty(this, "collectionTypeSource", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.collectionTypeSource = null;
        if (attr.collection_type) {
            this.collectionType = new CollectionTypeDescription(attr.collection_type);
        }
        else {
            this.collectionTypeSource = attr.collection_type_source;
            if (!this.collectionTypeSource) {
                console.log("Warning: No collection type or collection type source defined.");
            }
            this.collectionType = this.getCollectionTypeFromInput() || ANY_COLLECTION_TYPE_DESCRIPTION;
        }
        this.isCollection = true;
    }
    getCollectionTypeFromInput() {
        const connection = this.connectionStore.connections.find((connection) => connection.input.name === this.collectionTypeSource && connection.input.stepId === this.stepId);
        if (connection) {
            const outputStep = this.stepStore.getStep(connection.output.stepId);
            const inputStep = this.stepStore.getStep(this.stepId);
            assertDefined(inputStep, `Invalid step. Could not find step with id ${connection.input.stepId} in store.`);
            if (outputStep) {
                const stepOutput = outputStep.outputs.find((output) => output.name == connection.output.name);
                const stepInput = inputStep.inputs.find((input) => input.name === this.collectionTypeSource);
                if (stepInput && stepOutput) {
                    const outputTerminal = terminalFactory(connection.output.stepId, stepOutput, this.datatypesMapper, this.connectionStore, this.stepStore);
                    const inputTerminal = terminalFactory(connection.output.stepId, stepInput, this.datatypesMapper, this.connectionStore, this.stepStore);
                    // otherCollectionType is the mapped over output collection as it would appear at the input terminal
                    const otherCollectionType = inputTerminal._otherCollectionType(outputTerminal);
                    // we need to find which of the possible input collection types is connected
                    if ("collectionTypes" in inputTerminal) {
                        // collection_type_source must point at input collection terminal
                        const connectedCollectionType = inputTerminal.collectionTypes.find((collectionType) => otherCollectionType.canMatch(collectionType) ||
                            otherCollectionType.canMapOver(collectionType));
                        if (connectedCollectionType) {
                            if (connectedCollectionType.collectionType === "any") {
                                // if the input collection type is "any" this output's collection type
                                // is exactly the same as the connected output
                                return otherCollectionType;
                            }
                            else {
                                // else we pick the matching input collection type
                                // so that the map over logic applies correctly
                                return connectedCollectionType;
                            }
                        }
                    }
                }
            }
        }
        return ANY_COLLECTION_TYPE_DESCRIPTION;
    }
}
export class OutputParameterTerminal extends BaseOutputTerminal {
    constructor(attr) {
        super(Object.assign(Object.assign({}, attr), { datatypes: [] }));
        this.type = attr.type;
    }
}
export class InvalidOutputTerminal extends BaseOutputTerminal {
    constructor(attr) {
        super(attr);
        Object.defineProperty(this, "valid", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.valid = false;
    }
    validInputTerminals() {
        return [];
    }
    canAccept() {
        return new ConnectionAcceptable(false, "Can't connect to invalid terminal.");
    }
}
export function producesAcceptableDatatype(datatypesMapper, inputDatatypes, otherDatatypes) {
    for (const t in inputDatatypes) {
        const thisDatatype = inputDatatypes[t];
        if (thisDatatype === "input") {
            return new ConnectionAcceptable(true, null);
        }
        // FIXME: No idea what to do about case when datatype is 'input'
        const validMatch = otherDatatypes.some((otherDatatype) => otherDatatype === "input" ||
            otherDatatype === "_sniff_" ||
            datatypesMapper.isSubType(otherDatatype, thisDatatype));
        if (validMatch) {
            return new ConnectionAcceptable(true, null);
        }
    }
    const datatypesSet = new Set(datatypesMapper.datatypes);
    const invalidDatatypes = otherDatatypes.filter((datatype) => !datatypesSet.has(datatype));
    if (invalidDatatypes.length) {
        return new ConnectionAcceptable(false, `Effective output data type(s) [${invalidDatatypes.join(", ")}] unknown. This tool cannot be run on this Galaxy Server at this moment, please contact the Administrator.`);
    }
    return new ConnectionAcceptable(false, `Effective output data type(s) [${otherDatatypes.join(", ")}] do not appear to match input type(s) [${inputDatatypes.join(", ")}].`);
}
function isInvalidOutputArg(arg) {
    return "name" in arg && "valid" in arg && arg.valid === false;
}
function isOutputParameterArg(arg) {
    return "name" in arg && "parameter" in arg && arg.parameter === true;
}
function isOutputCollectionArg(arg) {
    return "name" in arg && "collection" in arg && arg.collection;
}
function isOutputArg(arg) {
    return "name" in arg && "extensions" in arg;
}
export function terminalFactory(stepId, terminalSource, datatypesMapper, connectionStore, stepStore) {
    if ("input_type" in terminalSource) {
        const terminalArgs = {
            datatypesMapper: datatypesMapper,
            input_type: terminalSource.input_type,
            name: terminalSource.name,
            stepId: stepId,
            connectionStore,
            stepStore,
        };
        if ("valid" in terminalSource) {
            return new InvalidInputTerminal(Object.assign(Object.assign({}, terminalArgs), { input: {
                    datatypes: [],
                    multiple: false,
                    optional: false,
                }, valid: terminalSource.valid }));
        }
        else {
            const inputArgs = {
                datatypes: terminalSource.extensions,
                multiple: terminalSource.multiple,
                optional: terminalSource.optional,
            };
            if (terminalSource.input_type == "dataset") {
                // type cast appears to be necessary: https://github.com/Microsoft/TypeScript/issues/13995
                return new InputTerminal(Object.assign(Object.assign({}, terminalArgs), { input: inputArgs }));
            }
            else if (terminalSource.input_type === "dataset_collection") {
                return new InputCollectionTerminal(Object.assign(Object.assign({}, terminalArgs), { collection_types: terminalSource.collection_types, input: Object.assign({}, inputArgs) }));
            }
            else if (terminalSource.input_type === "parameter") {
                return new InputParameterTerminal(Object.assign(Object.assign({}, terminalArgs), { type: terminalSource.type, input: Object.assign({}, inputArgs) }));
            }
        }
    }
    else if (terminalSource.name) {
        const outputArgs = {
            name: terminalSource.name,
            optional: terminalSource.optional,
            stepId: stepId,
            datatypesMapper: datatypesMapper,
            connectionStore,
            stepStore,
        };
        if (isOutputParameterArg(terminalSource)) {
            return new OutputParameterTerminal(Object.assign(Object.assign({}, outputArgs), { type: terminalSource.type }));
        }
        else if (isOutputCollectionArg(terminalSource)) {
            return new OutputCollectionTerminal(Object.assign(Object.assign({}, outputArgs), { datatypes: terminalSource.extensions, collection_type: terminalSource.collection_type, collection_type_source: terminalSource.collection_type_source }));
        }
        else if (isOutputArg(terminalSource)) {
            return new OutputTerminal(Object.assign(Object.assign({}, outputArgs), { datatypes: terminalSource.extensions }));
        }
    }
    if (isInvalidOutputArg(terminalSource)) {
        return new InvalidOutputTerminal(terminalSource);
    }
    throw Error(`Could not build terminal for ${terminalSource}`);
}
